// src/pages/AdminTools.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import useAccessProfile from "../hooks/useAccessProfile";
import { canManageRoles, hasRole } from "../utils/accessControl";
import "./AdminTools.css";

const cardStyle = {
  padding: 16,
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  background: "#fff",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
};

const labelStyle = {
  display: "block",
  marginBottom: 6,
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  minHeight: 44,
};

const helperStyle = {
  margin: "6px 0 0",
  fontSize: 12,
  color: "#64748b",
};

const primaryButtonStyle = {
  padding: "12px 14px",
  borderRadius: 8,
  border: "none",
  background: "#2563eb",
  color: "#fff",
  fontWeight: 700,
};

const secondaryButtonStyle = {
  padding: "12px 14px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 700,
};

const initialPartnerForm = {
  partnerName: "",
  partnerId: "",
  storeName: "",
  storeId: "",
  city: "",
  address: "",
};

function clean(value) {
  return String(value || "").trim();
}

const GOOGLE_MAPS_SCRIPT_ID = "google-maps-places-script";

function loadGoogleMapsPlacesApi(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google);
      return;
    }

    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&libraries=places`;

    script.onload = () => resolve(window.google);
    script.onerror = reject;

    document.head.appendChild(script);
  });
}

function getCityFromAddressComponents(components = []) {
  const preferredTypes = [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "administrative_area_level_1",
  ];

  for (const type of preferredTypes) {
    const match = components.find((item) => item.types?.includes(type));
    if (match?.long_name) return match.long_name;
  }

  return "";
}

export default function AdminTools() {
  const { currentUser } = useAuth();
  const { profile, loading: accessLoading } = useAccessProfile();

  const [runningPartnerCreate, setRunningPartnerCreate] = useState(false);
  const [out, setOut] = useState(null);
  const [partnerForm, setPartnerForm] = useState(initialPartnerForm);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [addressLookupStatus, setAddressLookupStatus] = useState("");

  const isAdmin = useMemo(() => {
    const email = String(currentUser?.email || profile?.email || "").toLowerCase();

    return (
      hasRole(profile, "admin") ||
      canManageRoles(profile) ||
      email.endsWith("@nexterzone.com")
    );
  }, [currentUser?.email, profile]);

  const canSubmitPartnerStore = useMemo(() => {
    return clean(partnerForm.partnerName) && clean(partnerForm.storeName);
  }, [partnerForm.partnerName, partnerForm.storeName]);
  
  useEffect(() => {
    if (accessLoading || !isAdmin) return;

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setAddressLookupStatus(
        "Address autocomplete is disabled because REACT_APP_GOOGLE_MAPS_API_KEY is missing."
      );
      return;
    }

    let cancelled = false;

    loadGoogleMapsPlacesApi(apiKey)
      .then(() => {
        if (cancelled || !addressInputRef.current || autocompleteRef.current) {
          return;
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            componentRestrictions: { country: "za" },
            fields: ["formatted_address", "name", "address_components"],
          }
        );

        autocompleteRef.current = autocomplete;
        setAddressLookupStatus("Address suggestions enabled.");

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();

          const selectedAddress = clean(
            place?.formatted_address ||
              place?.name ||
              addressInputRef.current?.value
          );

          const selectedCity = getCityFromAddressComponents(
            place?.address_components || []
          );

          setPartnerForm((prev) => ({
            ...prev,
            address: selectedAddress || prev.address,
            city: selectedCity || prev.city,
          }));
        });
      })
      .catch((err) => {
        console.error("Google Places autocomplete failed:", err);
        setAddressLookupStatus(
          "Address suggestions could not be loaded. You can still enter the address manually."
        );
      });

    return () => {
      cancelled = true;

      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [accessLoading, isAdmin]);

  if (accessLoading) {
    return <div style={{ padding: 16 }}>Loading admin access...</div>;
  }

  if (!isAdmin) {
    return <div style={{ padding: 16 }}>Not authorized.</div>;
  }

  const updatePartnerField = (field, value) => {
    setPartnerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetPartnerForm = () => {
    setPartnerForm(initialPartnerForm);
    setShowSuccess(false);
    setSuccessData(null);
    setErrorMessage("");
  };

  const createPartnerStore = async (event) => {
    event.preventDefault();

    if (!canSubmitPartnerStore) {
      alert("Partner Name and Store Name are required.");
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmCreatePartnerStore = async () => {
    setShowConfirmModal(false);
    setRunningPartnerCreate(true);
    setErrorMessage("");
    setShowSuccess(false);

    try {
      const functions = getFunctions(undefined, "us-central1");
      const seed = httpsCallable(functions, "seedPartnerDropoffLookups");

      const payload = {
        partnerName: clean(partnerForm.partnerName),
        partnerId: clean(partnerForm.partnerId) || undefined,
        storeName: clean(partnerForm.storeName),
        storeId: clean(partnerForm.storeId) || undefined,
        city: clean(partnerForm.city),
        address: clean(partnerForm.address),
      };

      const res = await seed(payload);

      setSuccessData(res.data);
      setShowSuccess(true);
      console.log("Partner store creation result:", res.data);

      resetPartnerForm();
    } catch (e) {
      console.error("Partner store creation failed:", e);
      setErrorMessage("We could not create the partner store. Please check the details and try again.");
    } finally {
      setRunningPartnerCreate(false);
    }
  };

  return (
    <div
      style={{
        padding: 16,
        maxWidth: 900,
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <header
        style={{
          marginBottom: 18,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#2563eb",
            }}
          >
            Admin Console
          </div>

          <h2 style={{ margin: "4px 0 6px", color: "#0f172a" }}>
            Partner Store Setup
          </h2>

          <p style={{ margin: 0, color: "#64748b" }}>
            Create partner and drop-off point lookup records for User Access
            assignment.
          </p>
        </div>

        <div
          style={{
            padding: "8px 12px",
            borderRadius: 999,
            background: "#e0f2fe",
            color: "#075985",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {currentUser?.email || profile?.email || "Admin"}
        </div>
      </header>

      <section style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", color: "#0f172a" }}>
          Create Partner Store
        </h3>

        <p style={{ color: "#64748b", marginTop: 0 }}>
          Creates or updates the Firestore lookup records used by the User
          Access partner and store dropdowns.
        </p>

        <div
          style={{
            padding: 12,
            borderRadius: 10,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e3a8a",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          After the partner user creates a profile, create the partner store
          here, then go to User Access, search their email, assign Partner
          Inspector, and select this partner/store.
        </div>

        <form onSubmit={createPartnerStore}>
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
            }}
          >
            <div>
              <label style={labelStyle} htmlFor="partnerName">
                Partner Name *
              </label>
              <input
                id="partnerName"
                type="text"
                placeholder="Example: 3@1 Newmarket"
                value={partnerForm.partnerName}
                onChange={(e) =>
                  updatePartnerField("partnerName", e.target.value)
                }
                style={inputStyle}
                disabled={runningPartnerCreate}
              />
            </div>

            <div>
              <label style={labelStyle} htmlFor="partnerId">
                Partner ID
              </label>
              <input
                id="partnerId"
                type="text"
                placeholder="Optional, example: 3at1-newmarket"
                value={partnerForm.partnerId}
                onChange={(e) =>
                  updatePartnerField("partnerId", e.target.value)
                }
                style={inputStyle}
                disabled={runningPartnerCreate}
              />
              <p style={helperStyle}>
                Leave blank to let the backend generate this from the partner
                name.
              </p>
            </div>

            <div>
              <label style={labelStyle} htmlFor="storeName">
                Store / Drop-off Point Name *
              </label>
              <input
                id="storeName"
                type="text"
                placeholder="Example: 3@1 Newmarket"
                value={partnerForm.storeName}
                onChange={(e) =>
                  updatePartnerField("storeName", e.target.value)
                }
                style={inputStyle}
                disabled={runningPartnerCreate}
              />
            </div>

            <div>
              <label style={labelStyle} htmlFor="storeId">
                Store ID
              </label>
              <input
                id="storeId"
                type="text"
                placeholder="Optional, example: 3at1-newmarket-store"
                value={partnerForm.storeId}
                onChange={(e) => updatePartnerField("storeId", e.target.value)}
                style={inputStyle}
                disabled={runningPartnerCreate}
              />
              <p style={helperStyle}>
                Leave blank to let the backend generate this as partner-id-store.
              </p>
            </div>

            <div>
              <label style={labelStyle} htmlFor="city">
                City
              </label>
              <input
                id="city"
                type="text"
                placeholder="Example: Alberton"
                value={partnerForm.city}
                onChange={(e) => updatePartnerField("city", e.target.value)}
                style={inputStyle}
                disabled={runningPartnerCreate}
              />
            </div>

            <div>
              <label style={labelStyle} htmlFor="address">
                Address
              </label>
              <input
                ref={addressInputRef}
                id="address"
                type="text"
                placeholder="Start typing address, mall, or store location"
                value={partnerForm.address}
                onChange={(e) => updatePartnerField("address", e.target.value)}
                style={inputStyle}
                disabled={runningPartnerCreate}
                autoComplete="off"
              />

              <p style={helperStyle}>
                {addressLookupStatus ||
                  "Start typing a few characters, then select the correct address from the dropdown."}
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 16,
            }}
          >
            <button
              type="submit"
              disabled={runningPartnerCreate || !canSubmitPartnerStore}
              className="nz-touch-min"
              style={{
                ...primaryButtonStyle,
                cursor:
                  runningPartnerCreate || !canSubmitPartnerStore
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  runningPartnerCreate || !canSubmitPartnerStore ? 0.65 : 1,
              }}
            >
              {runningPartnerCreate ? "Creating partner store..." : "Create Partner Store"}
            </button>

            <button
              type="button"
              onClick={resetPartnerForm}
              disabled={runningPartnerCreate}
              className="nz-touch-min"
              style={{
                ...secondaryButtonStyle,
                cursor: runningPartnerCreate ? "not-allowed" : "pointer",
                opacity: runningPartnerCreate ? 0.65 : 1,
              }}
            >
              Clear
            </button>

            <button
              type="button"
              onClick={() => {
                window.location.href = "/admin/user-access";
              }}
              disabled={runningPartnerCreate}
              className="nz-touch-min"
              style={{
                ...secondaryButtonStyle,
                cursor: runningPartnerCreate ? "not-allowed" : "pointer",
                opacity: runningPartnerCreate ? 0.65 : 1,
              }}
            >
              Go to User Access
            </button>
          </div>
        </form>
      </section>

      {errorMessage && (
        <div className="error-banner">
          {errorMessage}
        </div>
      )}

      {showSuccess && successData && (
        <section className="success-card">
          <h3 className="success-title">Partner store created successfully</h3>
          <p className="success-message">
            The partner and drop-off point records are now available for user access assignment.
          </p>
          <div className="success-details">
            <div className="detail-item">
              <span className="detail-label">Partner Name:</span>
              <span className="detail-value">{successData.partnerName}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Partner ID:</span>
              <span className="detail-value">{successData.partnerId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Store Name:</span>
              <span className="detail-value">{successData.storeName}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Store ID:</span>
              <span className="detail-value">{successData.storeId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">City:</span>
              <span className="detail-value">{successData.city}</span>
            </div>
          </div>
          <div className="success-buttons">
            <button
              onClick={() => {
                window.location.href = "/admin/user-access";
              }}
              className="nz-touch-min"
              style={primaryButtonStyle}
            >
              Go to User Access
            </button>
            <button
              onClick={resetPartnerForm}
              className="nz-touch-min"
              style={secondaryButtonStyle}
            >
              Create Another Store
            </button>
          </div>
        </section>
      )}

      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Create new partner store?</h3>
            <p className="modal-body">
              This will create or update the partner store and drop-off point records used for partner inspector assignment.
            </p>
            <div className="modal-buttons">
              <button
                onClick={confirmCreatePartnerStore}
                className="nz-touch-min"
                style={primaryButtonStyle}
              >
                Create Partner Store
              </button>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="nz-touch-min"
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}